/**
 * Where a finished portal sign-in may land: a path on this portal, never
 * anywhere else, never back into the sign-in pages. Dependency-free — the
 * API's twin (services/api-service/src/routes/auth/company-portal-sign-in.ts)
 * is held to the same cases by tests/architecture/company-web-mirrors.test.ts.
 */
/**
 * Only a path on this portal — never another origin, however spelled. Mirrors
 * the portal server's `safeReturnTo` (routes/auth/company-portal-sign-in.ts).
 */
export function safeReturnTo(value: unknown): string {
  if (typeof value !== 'string' || value.length > 2048 || !value.startsWith('/')) return '/';
  // A backslash reads as `/` to a browser (`/\evil.example` → `//evil.example`),
  // and control characters are stripped from URLs or break the Location
  // header; neither belongs in a path we hand back.
  for (const ch of value) {
    const code = ch.charCodeAt(0);
    if (ch === '\\' || code < 0x20 || code === 0x7f) return '/';
  }
  // Resolve against a placeholder origin: anything that leaves it (`//host`,
  // `/\host`, …) is not a path on this portal.
  const base = 'https://portal.invalid';
  let url: URL;
  try {
    url = new URL(value, base);
  } catch {
    return '/';
  }
  if (url.origin !== base) return '/';
  // Dot segments can leave a path that starts with `//` while the origin still
  // matches (`/.//evil.example` resolves to the path `//evil.example`), and a
  // browser reads that as another host. Idempotent, not safe-because-reapplied.
  if (url.pathname.startsWith('//')) return '/';
  // Never back into the sign-in pages themselves: `/auth/login?returnTo=/auth/login`
  // would send a signed-in visitor from `/auth/login` to `/auth/login`, and
  // loop. Decided on the RESOLVED path, so `/a/../auth/login` is caught too.
  if (url.pathname === '/auth' || url.pathname.startsWith('/auth/')) return '/';
  return `${url.pathname}${url.search}${url.hash}`;
}
